import { Link } from "react-router-dom";
import { CalendarClock, GraduationCap, Trophy, Award, ArrowRight } from "lucide-react";
import { useUpcomingEvents } from "@/hooks/useUpcomingEvents";
import type { EventType } from "@/types/registeredStudent";

const TYPE_ICON: Record<EventType, typeof CalendarClock> = {
  BOARD_EXAM: GraduationCap,
  COMPETITIVE_EXAM: GraduationCap,
  OLYMPIAD: Trophy,
  SCHOLARSHIP: Award,
  COMPETITION: Trophy,
};

function daysUntil(iso: string): number {
  const target = new Date(iso + "T00:00:00").getTime();
  const now = new Date(); now.setHours(0, 0, 0, 0);
  return Math.round((target - now.getTime()) / 86400000);
}

export function ComingUpPanel({ subject, gradeBand, curriculum, onFindTeachers }: { subject?: string | null; gradeBand?: string | null; curriculum?: string | null; onFindTeachers?: (q: string) => void }) {
  const { events, isLoading } = useUpcomingEvents({ subject, gradeBand, curriculum });
  const visible = events.slice(0, 4);

  return (
    <div className="rounded-xl bg-slate-100/80 overflow-hidden">
      <div className="px-4 py-3 flex items-center gap-2 bg-sky-50/60 border-b border-sky-100/60">
        <span className="inline-grid place-items-center w-6 h-6 rounded-md bg-sky-100 text-sky-600 shrink-0"><CalendarClock className="w-3.5 h-3.5" /></span>
        <span className="text-sm font-semibold text-slate-800">Upcoming Exams/Events</span>
      </div>

      {isLoading ? (
        <div className="animate-pulse divide-y divide-slate-100">
          {[0, 1, 2].map((i) => (
            <div key={i} className="px-4 py-3">
              <div className="h-3 bg-slate-100 rounded w-3/4 mb-2" />
              <div className="h-3 bg-slate-100 rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : visible.length === 0 ? (
        <div className="px-4 py-6 text-center">
          <CalendarClock className="w-7 h-7 text-slate-200 mx-auto mb-2" />
          <p className="text-xs font-medium text-slate-400">
            {!subject ? "Set a subject goal to see upcoming events" : "No upcoming exams or events found"}
          </p>
          {!subject && (
            <Link to="/my-goal" className="mt-2 inline-block text-xs text-indigo-600 hover:text-indigo-700 font-semibold">
              Set your goal →
            </Link>
          )}
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {visible.map((e) => {
            const Icon = TYPE_ICON[e.type] ?? CalendarClock;
            const d = daysUntil(e.eventDate);
            return (
              <div key={e.id} className="px-4 py-3">
                <div className="flex items-start gap-2">
                  <Icon className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 leading-snug">{e.name}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{d} days · {new Date(e.eventDate).toLocaleDateString()}</div>
                  </div>
                </div>
                {onFindTeachers ? (
                  <button
                    type="button"
                    onClick={() => onFindTeachers(e.name)}
                    className="mt-1.5 ml-6 inline-flex items-center gap-0.5 text-xs text-indigo-600 hover:text-indigo-700 font-semibold transition-colors"
                  >
                    Find prep teachers <ArrowRight className="w-3 h-3" />
                  </button>
                ) : (
                  <Link
                    to={`/search?q=${encodeURIComponent(e.name)}`}
                    className="mt-1.5 ml-6 inline-flex items-center gap-0.5 text-xs text-indigo-600 hover:text-indigo-700 font-semibold transition-colors"
                  >
                    Find prep teachers <ArrowRight className="w-3 h-3" />
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
